/**
 * KosSaya Role Constants
 * Role names harus lowercase, sesuai response API.
 */
export enum AppRole {
    ADMINISTRATOR = 'administrator',
    OWNER         = 'owner',
    KEEPER        = 'keeper',
    RENTER        = 'renter'
}

/** Roles default yang masuk ke admin modules (sidebar layout) */
export const ADMIN_ROLES: string[] = [
    AppRole.ADMINISTRATOR,
    AppRole.OWNER,
    AppRole.KEEPER,
];

/** Roles yang masuk ke user modules (mobile layout) */
export const USER_ROLES: string[] = [
    AppRole.RENTER,
];

/**
 * Helper: Safely extracts string role name (lowercase) from string, array, or object.
 */
export function extractUserRole(userOrRole: any): string {
    if (!userOrRole) return '';
    if (typeof userOrRole === 'string') return userOrRole.toLowerCase();

    if (typeof userOrRole === 'object') {
        const role = userOrRole.role ?? userOrRole.roles ?? userOrRole;
        if (typeof role === 'string') return role.toLowerCase();
        if (Array.isArray(role) && role.length > 0) {
            const first = role[0];
            if (typeof first === 'string') return first.toLowerCase();
            if (typeof first === 'object' && first?.name) return String(first.name).toLowerCase();
        }
        if (typeof role === 'object' && role?.name) {
            return String(role.name).toLowerCase();
        }
    }

    return String(userOrRole).toLowerCase();
}

/**
 * Cek apakah role termasuk admin/staff (Owner/Keeper/Administrator/Custom Role).
 * User non-renter diarahkan ke dashboard admin.
 */
export function isAdminRole(role: any): boolean {
    const r = extractUserRole(role);
    return r !== AppRole.RENTER && r !== 'user';
}

/**
 * Cek apakah role termasuk user/renter.
 * Dipakai untuk redirect setelah login.
 */
export function isUserRole(role: any): boolean {
    const r = extractUserRole(role);
    return r === AppRole.RENTER;
}

/** Role yang boleh CRUD (tambah/edit/hapus data tenant) */
export function canEditData(role: any): boolean {
    const r = extractUserRole(role);
    return r === AppRole.OWNER || r === AppRole.ADMINISTRATOR;
}

/** Role Administrator (Superadmin) */
export function isAdmin(role: any): boolean {
    return extractUserRole(role) === AppRole.ADMINISTRATOR;
}

/** Role Owner */
export function isOwner(role: any): boolean {
    return extractUserRole(role) === AppRole.OWNER;
}

/** Role Keeper */
export function isKeeper(role: any): boolean {
    return extractUserRole(role) === AppRole.KEEPER;
}

/** Role Renter */
export function isRenter(role: any): boolean {
    return extractUserRole(role) === AppRole.RENTER;
}

/**
 * Helper: Cek apakah user memiliki permission yang dibutuhkan.
 * Superadmin (Administrator) selalu memiliki akses penuh (bypass).
 */
export function hasPermission(
    userPermissions: (string | { name?: string })[] = [],
    required: string | string[],
    userRole?: string
): boolean {
    const role = (userRole || '').toLowerCase();
    if (role === AppRole.ADMINISTRATOR) {
        return true;
    }

    if (!required || (Array.isArray(required) && required.length === 0)) {
        return true;
    }

    const reqList = Array.isArray(required) ? required : [required];
    const userPermsList = (userPermissions || []).map((p) =>
        (typeof p === 'string' ? p : p?.name || '').toLowerCase()
    );

    return reqList.some((req) => userPermsList.includes(req.toLowerCase()));
}

